import { useEffect, useState, type FormEvent } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@shared/contexts/AuthContext";
import { Button } from "@shared/components/ui/button";
import { Input } from "@shared/components/ui/input";
import { Label } from "@shared/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@shared/components/ui/dialog";
import {
  useCreateUser,
  useDeleteUser,
  useDisableUser,
  useEnableUser,
  useResetUserPassword,
  useUpdateUser,
  useUser,
} from "@shared/api/hooks/useUsers";
import { useDepartments, useTeams } from "@shared/api/hooks/useOrg";
import type { User } from "@shared/api/users";
import { ApiError } from "@shared/api/client";
import { ArrowLeft } from "lucide-react";
import { toast } from "sonner";

const ROLES: User["role"][] = ["admin", "editor", "analyst"];

// Generated password for the create + admin-reset flows. Uses the Web Crypto
// CSPRNG, not Math.random() — these values become staff login credentials, so
// their entropy is a real trust boundary. (Uint32 % 55 leaves only ~1e-8 modulo
// bias, negligible here.) Stronger still would be a server-side CSPRNG that
// returns the password once; left as a follow-up to avoid an API change.
function randomPassword(len = 14): string {
  const alphabet = "abcdefghijkmnpqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789";
  const bytes = new Uint32Array(len);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => alphabet[b % alphabet.length]).join("");
}

interface UserFormState {
  username: string;
  displayName: string;
  email: string;
  role: User["role"];
  departmentId: string;
  teamIds: string[];
  password: string;
}

const UserForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);
  const { isAdmin, username: myUsername } = useAuth();

  const { data: existing } = useUser(id);
  const { data: departments = [] } = useDepartments();
  const { data: teams = [] } = useTeams();

  const createMut = useCreateUser();
  const updateMut = useUpdateUser();
  const enableMut = useEnableUser();
  const disableMut = useDisableUser();
  const deleteMut = useDeleteUser();
  const resetMut = useResetUserPassword();

  // Wire format is camelCase (ADR-0020): API responses and request
  // bodies both use displayName / departmentId / teamIds. Keep the form
  // state camelCase too so there's no snake/camel mixing.
  const [form, setForm] = useState<UserFormState>({
    username: "",
    displayName: "",
    email: "",
    role: "editor",
    departmentId: "",
    teamIds: [],
    password: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  // Reset shows the generated temp password in a read-only, copy-only dialog.
  // It is system-generated and non-editable BY DESIGN: the old window.prompt
  // exposed an editable field, so an admin who typed their own value believed
  // they'd set it while the random password was actually stored — the user
  // then couldn't log in. What this dialog shows IS what was stored.
  const [resetPw, setResetPw] = useState<string | null>(null);

  useEffect(() => {
    if (!isAdmin) {
      toast.error("Admin access required");
      navigate("/staff/exhibitions");
    }
  }, [isAdmin, navigate]);

  useEffect(() => {
    if (existing) {
      setForm((f) => ({
        ...f,
        username: existing.username,
        displayName: existing.displayName,
        email: existing.email ?? "",
        role: existing.role,
        departmentId: existing.departmentId ?? "",
        teamIds: existing.teamIds ?? [],
      }));
    }
  }, [existing]);

  if (!isAdmin) return null;

  const isSelf = isEdit && existing && existing.username === myUsername;

  const toggleTeam = (teamId: string) => {
    setForm((f) => ({
      ...f,
      teamIds: f.teamIds.includes(teamId)
        ? f.teamIds.filter((t) => t !== teamId)
        : [...f.teamIds, teamId],
    }));
  };

  const apiErr = (err: unknown, fallback: string) =>
    toast.error(
      err instanceof ApiError && err.message ? err.message : fallback,
    );

  const handleCreate = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    createMut.mutate(
      {
        username: form.username,
        displayName: form.displayName,
        role: form.role,
        password: form.password,
        email: form.email || null,
        teamIds: form.teamIds,
        departmentId: form.departmentId || null,
      },
      {
        onSuccess: () => {
          toast.success(
            "User created. Communicate the initial password out-of-band; " +
              "the user must change it on first login.",
          );
          navigate("/staff/users");
        },
        onError: (err) => apiErr(err, "Failed to create user"),
      },
    );
  };

  const handleSave = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!id) return;
    updateMut.mutate(
      {
        userId: id,
        payload: {
          displayName: form.displayName,
          role: form.role,
          email: form.email || null,
          teamIds: form.teamIds,
          departmentId: form.departmentId || null,
        },
      },
      {
        onSuccess: () => {
          toast.success("User updated");
          navigate("/staff/users");
        },
        onError: (err) => apiErr(err, "Failed to update user"),
      },
    );
  };

  const handleToggleEnabled = () => {
    if (!existing || !id) return;
    const mut = existing.enabled ? disableMut : enableMut;
    mut.mutate(id, {
      onSuccess: () =>
        toast.success(existing.enabled ? "User disabled" : "User enabled"),
      onError: (err) => apiErr(err, "Failed to update account status"),
    });
  };

  const handleReset = () => {
    if (!id) return;
    const pw = randomPassword();
    resetMut.mutate(
      { userId: id, password: pw },
      {
        onSuccess: () => setResetPw(pw),
        onError: (err) => apiErr(err, "Failed to reset password"),
      },
    );
  };

  const copyResetPw = async () => {
    if (resetPw === null) return;
    try {
      await navigator.clipboard.writeText(resetPw);
      toast.success("Temporary password copied");
    } catch {
      toast.error("Copy failed — select the text and copy it manually");
    }
  };

  const handleDelete = () => {
    if (!id) return;
    if (
      !window.confirm("Soft-delete this user? This is reversible only via DB.")
    ) {
      return;
    }
    deleteMut.mutate(id, {
      onSuccess: () => {
        toast.success("User deleted");
        navigate("/staff/users");
      },
      onError: (err) => apiErr(err, "Failed to delete user"),
    });
  };

  return (
    <div className="min-h-screen bg-background" data-testid="user-form-page">
      <div className="bg-white border-b border-border">
        <div className="max-w-2xl mx-auto px-6 py-4 flex items-center gap-3">
          <Button
            onClick={() => navigate("/staff/users")}
            variant="ghost"
            size="sm"
            data-testid="back-button"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <h1 className="text-2xl font-heading font-bold">
            {isEdit ? `Edit user: ${form.username}` : "Create new user"}
          </h1>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-6 py-8">
        <form
          onSubmit={isEdit ? handleSave : handleCreate}
          className="space-y-6 bg-white p-6 border border-border rounded-lg"
        >
          <div className="space-y-2">
            <Label htmlFor="username">Username</Label>
            <Input
              id="username"
              value={form.username}
              onChange={(e) => setForm({ ...form, username: e.target.value })}
              required
              disabled={isEdit}
              data-testid="username-input"
              className="h-12"
            />
            {isEdit && (
              <p className="text-xs text-muted-foreground">
                Username is permanent and cannot be changed.
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="display_name">Display name</Label>
            <Input
              id="display_name"
              value={form.displayName}
              onChange={(e) =>
                setForm({ ...form, displayName: e.target.value })
              }
              required
              data-testid="display-name-input"
              className="h-12"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="email">Email (optional)</Label>
            <Input
              id="email"
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              data-testid="email-input"
              className="h-12"
            />
            <p className="text-xs text-muted-foreground">
              Contact only — not used for login or password distribution.
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="role">Role</Label>
            <select
              id="role"
              value={form.role}
              onChange={(e) =>
                setForm({ ...form, role: e.target.value as User["role"] })
              }
              disabled={isSelf}
              data-testid="role-select"
              className="h-12 w-full border border-input rounded-md px-3 bg-background"
            >
              {ROLES.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
            {isSelf && (
              <p className="text-xs text-muted-foreground">
                You cannot change your own role.
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="department">Department</Label>
            <select
              id="department"
              value={form.departmentId}
              onChange={(e) =>
                setForm({ ...form, departmentId: e.target.value })
              }
              data-testid="department-select"
              className="h-12 w-full border border-input rounded-md px-3 bg-background"
            >
              <option value="">— none —</option>
              {departments.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-2">
            <Label>Teams</Label>
            <div
              className="flex flex-wrap gap-3"
              data-testid="teams-checkboxes"
            >
              {teams.length === 0 && (
                <span className="text-xs text-muted-foreground">
                  No teams defined yet.
                </span>
              )}
              {teams.map((t) => (
                <label key={t.id} className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={form.teamIds.includes(t.id)}
                    onChange={() => toggleTeam(t.id)}
                    data-testid={`team-checkbox-${t.id}`}
                  />
                  {t.name}
                  <span className="text-muted-foreground">
                    {" · "}
                    {departments.find((d) => d.id === t.departmentId)?.name ??
                      "no department"}
                  </span>
                </label>
              ))}
            </div>
          </div>

          {!isEdit && (
            <div className="space-y-2">
              <Label htmlFor="password">Initial password</Label>
              <div className="flex gap-2">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={form.password}
                  onChange={(e) =>
                    setForm({ ...form, password: e.target.value })
                  }
                  required
                  minLength={8}
                  data-testid="password-input"
                  className="h-12"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setForm((f) => ({ ...f, password: randomPassword() }));
                    setShowPassword(true);
                  }}
                  data-testid="generate-password-button"
                >
                  Generate
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Min 8 characters. Generate is recommended. The user is forced to
                change it on first login.
              </p>
            </div>
          )}

          <div className="flex flex-wrap gap-3 pt-2">
            <Button
              type="submit"
              data-testid="submit-button"
              disabled={createMut.isPending || updateMut.isPending}
            >
              {isEdit ? "Save changes" : "Create user"}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate("/staff/users")}
              data-testid="cancel-button"
            >
              Cancel
            </Button>
          </div>

          {isEdit && existing && (
            <div
              className="border-t border-border pt-4 mt-2 space-y-3"
              data-testid="edit-actions"
            >
              <p className="text-sm font-medium">Account actions</p>
              <div className="flex flex-wrap gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleToggleEnabled}
                  disabled={isSelf}
                  data-testid="toggle-enabled-button"
                >
                  {existing.enabled ? "Disable account" : "Enable account"}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleReset}
                  data-testid="reset-password-button"
                >
                  Reset password…
                </Button>
                <Button
                  type="button"
                  variant="destructive"
                  onClick={handleDelete}
                  disabled={isSelf}
                  data-testid="soft-delete-button"
                >
                  Soft-delete user…
                </Button>
              </div>
              {isSelf && (
                <p className="text-xs text-muted-foreground">
                  You cannot disable or delete your own account.
                </p>
              )}
            </div>
          )}
        </form>
      </div>

      <Dialog
        open={resetPw !== null}
        onOpenChange={(open) => !open && setResetPw(null)}
      >
        <DialogContent data-testid="reset-password-dialog">
          <DialogHeader>
            <DialogTitle>Temporary password set</DialogTitle>
            <DialogDescription>
              Copy this password and send it to the user out-of-band. It is
              system-generated and shown only once here — the user must change
              it on first login.
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-2">
            <Input
              readOnly
              value={resetPw ?? ""}
              onFocus={(e) => e.target.select()}
              data-testid="reset-password-value"
              className="h-12 font-mono"
            />
            <Button
              type="button"
              variant="outline"
              onClick={copyResetPw}
              data-testid="copy-reset-password-button"
            >
              Copy
            </Button>
          </div>
          <DialogFooter>
            <Button
              type="button"
              onClick={() => setResetPw(null)}
              data-testid="reset-password-done-button"
            >
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UserForm;
